/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { CachingPolicy } from '../cachingpolicy/CachingPolicy';
import { InternalRatingsServiceRepositoryImpl } from '../impls/InternalRatingsServiceRepositoryImpl';
import { IInternalRatingService } from '../interfaces/IInternalRatingsService';
import { IInternalRatingsServiceRepository } from '../interfaces/IInternalRatingsServiceRepository';
import { Log } from '../logging';
import { Storage } from '../storage';
import { TExtendedRatingLimits, TServicesParams } from '../types';
import { InternalRatingsUtility } from '../utils';

export class InternalRatingsService implements IInternalRatingService {
    private oInternalRatingsServiceRepository: IInternalRatingsServiceRepository;

    private TAG: string = 'InternalRatingsService';

    private static INTERNAL_RATINGS_MAP: Map<string, string> = new Map();

    public constructor(params: TServicesParams) {
        this.oInternalRatingsServiceRepository = new InternalRatingsServiceRepositoryImpl(params);
    }

    public async fetchAndStoreInternalRatingsData(): Promise<void> {
        try {
            const currentTime = Date.now();
            const internalRatingsLastCallTime = Storage.getInstance().getItem(
                Storage.Keys.INTERNAL_RATINGS_SERVICE_LAST_CALL_TIME,
            );
            let internalRatingsData = Storage.getInstance().getItem(
                Storage.Keys.INTERNAL_RATINGS_SERVICE_DATA,
            );
            const isServiceCallRequired: boolean =
                !internalRatingsLastCallTime ||
                internalRatingsLastCallTime + CachingPolicy.InternalRatingsService.cachingDuration <
                    currentTime;
            const isDataAvailable: boolean = internalRatingsData;

            if (isServiceCallRequired || !isDataAvailable) {
                internalRatingsData =
                    await this.oInternalRatingsServiceRepository.fetchInternalRatingsFromService();
            }

            if (internalRatingsData?.ratingValue)
                InternalRatingsService.INTERNAL_RATINGS_MAP = new Map(
                    internalRatingsData.ratingValue.map(({ ratingTypeId, ratingValueId, name }) => {
                        return [ratingTypeId + ratingValueId.replace('type', 'tivo'), name];
                    }),
                );

            const isRatingTypeComplete =
                InternalRatingsUtility.getRequiredFields().ratingType.every((field) => {
                    return Object.prototype.hasOwnProperty.call(
                        internalRatingsData.ratingType[0],
                        field,
                    );
                });

            // Check and append missing fields in ratingType
            if (!isRatingTypeComplete) {
                const missingInternalFields = InternalRatingsUtility.getMissingRatingFields();
                internalRatingsData.ratingType.forEach((type) => {
                    const expectedFields = missingInternalFields.ratingType[type.ratingTypeId];
                    Object.keys(expectedFields).forEach((key) => {
                        if (!(key in type)) {
                            // eslint-disable-next-line no-param-reassign
                            type[key] = expectedFields[key];
                        }
                    });
                });
            }

            const isRatingValueComplete =
                InternalRatingsUtility.getRequiredFields().ratingValue.every((field) => {
                    return Object.prototype.hasOwnProperty.call(
                        internalRatingsData.ratingValue[0],
                        field,
                    );
                });

            // Check and append missing fields in ratingValue
            if (!isRatingValueComplete) {
                const missingInternalFields = InternalRatingsUtility.getMissingRatingFields();
                internalRatingsData.ratingValue.forEach((value) => {
                    const expectedFields = missingInternalFields.ratingValue[value.name];
                    Object.keys(expectedFields).forEach((key) => {
                        if (!(key in value)) {
                            // eslint-disable-next-line no-param-reassign
                            value[key] = expectedFields[key];
                        }
                    });
                });
            }

            Storage.getInstance().setItem(
                Storage.Keys.INTERNAL_RATINGS_SERVICE_DATA,
                internalRatingsData,
            );
        } catch (error) {
            Log.e(this.TAG, 'fetchAndStoreBodyInfoData', error);
            throw error;
        }
    }

    public getInternalRatingName(ratingKey: string): string {
        try {
            return InternalRatingsService.INTERNAL_RATINGS_MAP.get(ratingKey) || '';
        } catch (error) {
            Log.e(this.TAG, 'fetchInternalRatings', error);
            throw error;
        }
    }

    public getInternalRatingsData(): TExtendedRatingLimits {
        try {
            return Storage.getInstance().getItem(Storage.Keys.INTERNAL_RATINGS_SERVICE_DATA);
        } catch (error) {
            Log.e(this.TAG, 'getInternalRatingsData', error);
            throw error;
        }
    }
}
